import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  "Mobile App",
  "Website / Landing Page",
  "SaaS / MVP",
  "CRM",
  "AI Agent / Chatbot",
  "Automation",
  "Dashboard / Analytics",
  "Tech Consultation",
  "Other",
];

const budgets = [
  "Under $5k",
  "$5k – $15k",
  "$15k – $40k",
  "$40k – $100k",
  "$100k+",
  "Not sure yet",
];

const initialState = {
  name: "",
  email: "",
  phone: "",
  company: "",
  project_type: "",
  budget: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error' | null
  const [errorMsg, setErrorMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus("error");
      setErrorMsg("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    setStatus(null);
    setErrorMsg("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(initialState);
      toast.success("Message sent — we'll be in touch within 24 hours.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data) ? err.response.data[0]?.msg : null) ||
        "Something went wrong. Please try again.";
      setStatus("error");
      setErrorMsg(typeof detail === "string" ? detail : "Submission failed.");
      toast.error("Could not send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={CONTACT.root}
      className="relative py-24 md:py-36 bg-[#FDFCFB]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="md:col-span-5"
        >
          <div className="caption mb-6">Contact</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05]">
            Start a <span className="italic">conversation.</span>
          </h2>
          <p className="mt-6 text-[#6B6A68] max-w-md leading-relaxed">
            Tell us what you're building (or thinking about). We reply to every
            serious enquiry within one business day.
          </p>

          <div className="mt-12 space-y-8 text-sm">
            <div>
              <div className="caption mb-2">Phone / WhatsApp</div>
              <a
                href="tel:+919873117605"
                className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] hover:underline underline-offset-4"
              >
                +91 98731 17605
              </a>
            </div>
            <div>
              <div className="caption mb-2">Email</div>
              <a
                href="mailto:hello@heritexinnovation.com"
                className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] hover:underline underline-offset-4 break-all"
              >
                hello@heritexinnovation.com
              </a>
            </div>
            <div>
              <div className="caption mb-2">Studio</div>
              <div className="font-serif-display text-xl md:text-2xl text-[#1A1A1A]">
                Mumbai, India
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onSubmit={submit}
          data-testid={CONTACT.form}
          className="md:col-span-7 md:pt-4"
          noValidate
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2">
            <div>
              <label className="caption block mb-1">Name *</label>
              <input
                data-testid={CONTACT.name}
                className="editorial-input"
                placeholder="Your full name"
                value={form.name}
                onChange={set("name")}
                required
                disabled={submitting}
              />
            </div>
            <div>
              <label className="caption block mb-1">Email *</label>
              <input
                data-testid={CONTACT.email}
                type="email"
                className="editorial-input"
                placeholder="you@company.com"
                value={form.email}
                onChange={set("email")}
                required
                disabled={submitting}
              />
            </div>
            <div>
              <label className="caption block mb-1">Phone</label>
              <input
                data-testid={CONTACT.phone}
                className="editorial-input"
                placeholder="+91 …"
                value={form.phone}
                onChange={set("phone")}
                disabled={submitting}
              />
            </div>
            <div>
              <label className="caption block mb-1">Company</label>
              <input
                data-testid={CONTACT.company}
                className="editorial-input"
                placeholder="Company / Brand"
                value={form.company}
                onChange={set("company")}
                disabled={submitting}
              />
            </div>
            <div>
              <label className="caption block mb-1">Project type</label>
              <select
                data-testid={CONTACT.projectType}
                className="editorial-input"
                value={form.project_type}
                onChange={set("project_type")}
                disabled={submitting}
              >
                <option value="">Select…</option>
                {projectTypes.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="caption block mb-1">Budget</label>
              <select
                data-testid={CONTACT.budget}
                className="editorial-input"
                value={form.budget}
                onChange={set("budget")}
                disabled={submitting}
              >
                <option value="">Select…</option>
                {budgets.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 mt-4">
              <label className="caption block mb-1">Tell us about the project *</label>
              <textarea
                data-testid={CONTACT.message}
                className="editorial-input"
                placeholder="A few lines on what you're building, timelines, and anything specific…"
                value={form.message}
                onChange={set("message")}
                rows={4}
                required
                disabled={submitting}
              />
            </div>
          </div>

          {status === "error" && (
            <div
              data-testid={CONTACT.error}
              className="mt-6 flex items-start gap-3 text-sm text-[#B03A2E]"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}
          {status === "success" && (
            <div
              data-testid={CONTACT.success}
              className="mt-6 flex items-start gap-3 text-sm text-[#3E5B42]"
            >
              <Check className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Thank you — your message has landed. We'll be in touch within 24 hours.</span>
            </div>
          )}

          <button
            data-testid={CONTACT.submit}
            type="submit"
            disabled={submitting}
            className="mt-10 w-full group inline-flex items-center justify-center gap-3 px-8 py-5 rounded-full bg-[#1A1A1A] text-[#FDFCFB] text-sm tracking-wide hover:bg-[#333] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending…" : "Send message"}
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </motion.form>
      </div>
    </section>
  );
}
